import deputy from '@webapp-next/lib/request/deputy';
import internalGet from '../user/internal/get'; // TODO: verify this call
import { DateTime } from 'luxon';
import { convertOnboardingStatusFilterToState } from '@webapp-next/modules/people/composables/hr-onboarding-status';
import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';
import { WebappApiResponse } from '@/lib/types/request/request';
import Employee from '@/modules/people/types/employee';
import { post } from '@webapp-next/lib/request/webapp/http';
import { AxiosRequestConfig } from 'axios';

const url = '/supervise/employee';

export type SearchFilters = {
  company?: string;
  active?: string;
  status?: string;
  payrollid?: string;
  role?: string;
  trainingexpiry?: string;
  training?: string;
  leavebalance?: string[];
  stressprofile?: string;
  device?: string;
  twoFactor?: string;
  onboardingStatus?: string[];
  employeeIdsUsingContracts: Employee['Id'][];
  /**
   * One id array per selected title, resolved from the selected title ids at
   * query time; flattened into a single IN clause.
   */
  employeeIdsUsingJobTitles?: Employee['Id'][][];
  /**
   * The job title filter selection, held by title id because that is the only
   * stable identity a title has. Resolved to `employeeIdsUsingJobTitles`
   * against the current options before the request is built, so it is never
   * sent as a query param itself.
   */
  jobTitleIds?: string[];
  mixSearch?: string;
  max?: number;
  start?: number;
  excludeIds?: number[];
  email?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  mobile?: string;
  id?: number;
  exposeMeta?: boolean;
  internal?: boolean;
  length?: number;
  sort?: Record<string, 'asc' | 'desc'>;
  THUMBNAIL_WIDTH?: number;
  dpMetaOption?: boolean;
  queryBasicData?: boolean;
};

type SearchQuery = {
  exposeMeta: boolean;
  start?: number;
  sort?: Record<string, unknown>;
  search?: Record<string, unknown>;
  max?: number;
  queryBasicData?: boolean;
};

export type EmployeesGetResponse = Promise<
  WebappApiResponse<Partial<Employee[] | Employee>>
>;

export type EmployeesApiHeaders = {
  'Image-Preview-Dimension'?: number;
  'dp-meta-option'?: 'none';
};

export type EmployeesOptions = AxiosRequestConfig & {
  headers?: EmployeesApiHeaders;
  // signal?: AbortController['signal'];
};

// https://deputy.atlassian.net/wiki/spaces/DASH/pages/338068931/People+tab+search+filter+API#Peopletabsearch/filterAPI-InvitationStatus
export default async function employeesGet(
  params?: SearchFilters,
  options?: EmployeesOptions
): EmployeesGetResponse {
  const {
    status: invitationStatus,
    mixSearch,
    active,
    company,
    role,
    stressprofile,
    training,
    leavebalance,
    payrollid,
    trainingexpiry,
    device,
    twoFactor,
    onboardingStatus,
    employeeIdsUsingContracts,
    employeeIdsUsingJobTitles,
    max,
    start = 0,
    excludeIds,
    email,
    displayName,
    firstName,
    lastName,
    mobile,
    id,
    exposeMeta = false,
    internal = false,
    sort = { DisplayName: 'asc' },
    THUMBNAIL_WIDTH = 135,
    dpMetaOption = false,
    queryBasicData = false,
  } = params || {};

  if (internal) {
    // TODO: remove this supress after migrating '../user/internal/get' to TS
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return internalGet(params);
  }

  // Set headers
  options = options || {};
  options.headers = options.headers || {};
  options.headers['Image-Preview-Dimension'] = THUMBNAIL_WIDTH;

  if (dpMetaOption === false) {
    options.headers['dp-meta-option'] = 'none';
  }

  const searchQuery: SearchQuery = {
    exposeMeta,
    start,
  };

  // Order results based on Display Name
  searchQuery.sort = { DisplayName: 'asc' };

  if (max) {
    searchQuery.max = max;
  }

  if (queryBasicData) {
    searchQuery.queryBasicData = queryBasicData;
  }
  // expose search API
  if (!mixSearch && email) {
    searchQuery.search = searchQuery.search || {};

    searchQuery.search.Email = {
      field: 'Email1',
      type: 'eq',
      data: email,
      join: 'ContactObject',
    };

    searchQuery.sort = { DisplayName: 'asc' };
  }

  if (!mixSearch && mobile) {
    searchQuery.search = searchQuery.search || {};

    searchQuery.search.Mobile = {
      field: 'Mobile',
      type: 'lk',
      data: `%${mobile}%`,
      join: 'DeputecUserAsEmployeeProfile',
      operationGroup: 'or',
    };
  }

  if (!mixSearch && displayName) {
    searchQuery.search = searchQuery.search || {};

    searchQuery.search.DisplayName = {
      field: 'DisplayName',
      type: 'lk',
      data: `%${displayName}%`,
      operationGroup: 'or',
    };
  }

  if (!mixSearch && firstName) {
    searchQuery.search = searchQuery.search || {};

    searchQuery.search.FirstName = {
      field: 'FirstName',
      type: 'lk',
      data: `%${firstName}%`,
      operationGroup: 'or',
    };
  }

  if (!mixSearch && lastName) {
    searchQuery.search = searchQuery.search || {};

    searchQuery.search.LastName = {
      field: 'LastName',
      type: 'lk',
      data: `%${lastName}%`,
      operationGroup: 'or',
    };
  }

  if (mixSearch) {
    searchQuery.search = searchQuery.search || {};

    searchQuery.search.DisplayName = {
      field: 'DisplayName',
      type: 'lk',
      data: `%${mixSearch}%`,
      operationGroup: 'or',
    };
    searchQuery.search.FirstName = {
      field: 'FirstName',
      type: 'lk',
      data: `%${mixSearch}%`,
      operationGroup: 'or',
    };
    searchQuery.search.LastName = {
      field: 'LastName',
      type: 'lk',
      data: `%${mixSearch}%`,
      operationGroup: 'or',
    };
    searchQuery.search.EmailAddress = {
      field: 'EmailAddress',
      type: 'lk',
      data: `%${mixSearch}%`,
      join: 'DeputecUserAsEmployeeProfile',
      operationGroup: 'or',
    };
    searchQuery.search.Mobile = {
      field: 'Mobile',
      type: 'lk',
      data: `%${mixSearch}%`,
      join: 'DeputecUserAsEmployeeProfile',
      operationGroup: 'or',
    };
    searchQuery.search.PayrollId = {
      field: 'PayrollId',
      type: 'lk',
      data: `%${mixSearch}%`,
      join: 'DeputecEmployeeAgreementAsEmployee',
      operationGroup: 'or',
    };
  }

  if (company) {
    searchQuery.search = searchQuery.search || {};

    searchQuery.search.Workplace = {
      field: 'Company',
      type: 'eq',
      data: company,
      join: 'DeputecEmployeeWorkplaceAsEmployee',
    };
  }

  if (role) {
    searchQuery.search = searchQuery.search || {};

    searchQuery.search.Role = {
      field: 'Role',
      type: 'eq',
      data: role,
    };
  }

  if (stressprofile) {
    searchQuery.search = searchQuery.search || {};

    searchQuery.search.StressProfile = {
      field: 'StressProfile',
      type: 'eq',
      data: stressprofile,
    };
  }

  if (training) {
    searchQuery.search = searchQuery.search || {};

    searchQuery.search.Training = {
      field: 'Module',
      type: 'in',
      data: training,
      join: 'DeputecTrainingRecordAsEmployee',
    };
    searchQuery.search.TrainingActive = {
      field: 'Active',
      type: 'eq',
      data: 1,
      join: 'DeputecTrainingRecordAsEmployee',
    };
  }

  if (invitationStatus) {
    searchQuery.search = searchQuery.search || {};

    searchQuery.search.InvitationStatus = {
      field: 'Status',
      type: 'eq',
      data: invitationStatus,
      join: 'DeputecUserAsEmployeeProfile',
    };
  }

  if (leavebalance) {
    searchQuery.search = searchQuery.search || {};

    searchQuery.search.LeaveBalanceType = {
      field: 'LeaveRule',
      type: 'in',
      data: leavebalance,
      join: 'DeputecLeaveAccrualAsEmployee',
    };
  }

  if (payrollid !== undefined) {
    searchQuery.search = searchQuery.search || {};
    searchQuery.search.Has = {
      DeputecEmployeeAgreementAsEmployee: payrollid === '1',
    };
  }

  if (trainingexpiry) {
    searchQuery.search = searchQuery.search || {};

    switch (trainingexpiry) {
      // no expiry
      case '0': {
        searchQuery.search.TrainingExpiry = {
          field: 'ExpiryDate',
          type: 'ns',
          data: '',
          join: 'DeputecTrainingRecordAsEmployee',
        };
        break;
      }
      // expired
      case '1':
        searchQuery.search.TrainingExpiry = {
          field: 'ExpiryDate',
          type: 'lt',
          data: DateTime.local().toISODate(),
          join: 'DeputecTrainingRecordAsEmployee',
        };
        break;
      // expire in 30, 60 or 90 days
      case '2':
      case '3':
      case '4': {
        const numDays = {
          2: 30,
          3: 60,
          4: 90,
        };

        searchQuery.search.TrainingExpiryStart = {
          field: 'ExpiryDate',
          type: 'ge',
          data: DateTime.local().toISODate(),
          join: 'DeputecTrainingRecordAsEmployee',
        };
        searchQuery.search.TrainingExpiryEnd = {
          field: 'ExpiryDate',
          type: 'le',
          data: DateTime.local()
            .plus({ days: numDays[trainingexpiry] })
            .toISODate(),
          join: 'DeputecTrainingRecordAsEmployee',
        };
        break;
      }
    }
  }

  if (active || active === undefined) {
    searchQuery.search = searchQuery.search || {};

    searchQuery.search.Active = {
      field: 'Active',
      type: 'in',
      data: active === undefined ? [0, 1] : [active],
    };
  }

  if (device) {
    searchQuery.search = searchQuery.search || {};

    searchQuery.search.Device = {
      field: 'Device',
      type: 'eq',
      data: device,
    };
  }

  if (twoFactor !== undefined) {
    searchQuery.search = searchQuery.search || {};
    searchQuery.search.Has = {
      TwoFactorEnabled: twoFactor === '1',
    };
  }

  if (onboardingStatus) {
    searchQuery.search = searchQuery.search || {};
    searchQuery.search.Has = {
      ...(searchQuery.search.Has as Record<string, unknown>),
      OnboardingStatus: convertOnboardingStatusFilterToState(onboardingStatus),
    };
  }

  if (excludeIds) {
    searchQuery.search = searchQuery.search || {};
    searchQuery.search.Id = {
      field: 'Id',
      type: 'nn',
      data: excludeIds,
    };
  }

  if (employeeIdsUsingContracts) {
    searchQuery.search = searchQuery.search || {};
    searchQuery.search.employeesUsingContracts = {
      field: 'Id',
      type: 'in',
      data: flatten(employeeIdsUsingContracts),
    };
  }

  // Its own search key: reusing employeesUsingContracts would clobber the pay
  // rate clause in the same object. Distinct keys AND together.
  //
  // uniq because the selection is a union: today an employee holds one active
  // assignment so the per-title sets are disjoint, but once an employee can
  // hold several, overlapping selections would repeat ids in the IN payload.
  if (employeeIdsUsingJobTitles?.length) {
    const ids = uniq(flatten(employeeIdsUsingJobTitles));

    searchQuery.search = searchQuery.search || {};
    searchQuery.search.employeesUsingJobTitles = ids.length
      ? {
          field: 'Id',
          type: 'in',
          data: ids,
        }
      : // A selected title none of whose holders are visible filters the list
        // to nobody. An empty IN payload is read as no clause at all, which
        // would widen the list back to everyone instead — the one answer the
        // selection cannot mean. No employee id is below 1.
        {
          field: 'Id',
          type: 'lt',
          data: 0,
        };
  }

  if (!params?.length) {
    const queryUrl = id ? `${url}/${id}` : `${url}/QUERY`;

    // Do not use POST for fetching User details or else it will trigger a Pusher update
    if (id) {
      // TODO: remove this supress after migrating '@webapp-next/lib/request/deputy' to TS
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return deputy.get({
        url: queryUrl,
        body: searchQuery,
        signal: options?.signal,
      });
    }

    const { data, headers } = await post(
      '/api/v1/supervise/employee/QUERY',
      searchQuery,
      options
    );

    return {
      success: true,
      message: '',
      headers,
      data: data || [],
    };
  }

  // TODO: remove this supress after migrating '@webapp-next/lib/request/deputy' to TS
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return deputy.get({ url, query: params, signal: options?.signal });
}
